import { useCallback, useEffect, useRef, useState } from "react";
import MessagingThreadSender from "./MessagingThread/MessagingThreadSender";
import { ThreadMessage } from "../../../types";
import { useTrackScrollTop } from "../../../lib/hooks/TrackScrollTop.hook";
import { s } from "../../../lib/styler";
import MessagingThreadMessage from "./MessagingThread/MessagingThreadMessages/MessagingThreadMessage";
import { useSingleEffect } from "../../../lib/hooks/SingleEffect.hook";
import Loader from "../../commons/Loader";
import { MessagingThreadLoadOlderMessagesResponse } from "./MessagingThread.types";
import { SortedMap } from "../../../lib/util/SortedMap";
import { libMisc } from "../../../lib/misc";
import MessagingThreadMessageDateCategoryDisplay from "./MessagingThread/MessagingThreadMessages/MessagingThreadMessage/MessagingThreadMessageDateCategoryDisplay";
import MessagingThreadFailedActionMenu from "./MessagingThreadFailedActionMenu";
import { useAtom } from "jotai";
import { serviceSync } from "../../../services/sync.service";
import MessagingThreadViewImageMedia from "./MessagingThread/MessagingThreadViewImageMedia";

export type MessagingThreadProps = {
    messages: ThreadMessage[];
    onLoadOlderMessages?: () => Promise<MessagingThreadLoadOlderMessagesResponse>;
}

const MINIMUM_TEXTAREA_LINES = 4;
const MAXIMUM_TEXTAREA_LINES = 15;
const EXTRA_LINE_HEIGHT = 21;

export default function MessagingThread({ onLoadOlderMessages, messages }: MessagingThreadProps) {
    const [addonLines, setAddonLines] = useState(0);
    const [fixedToBottom, setFixedToBottom] = useState(true);
    const fixedToBottomRef = useRef(fixedToBottom);
    fixedToBottomRef.current = fixedToBottom;
    const [fixedToBottomShift, setFixedToBottomShift] = useState(0);
    const [inflatedHeight, setInflatedHeight] = useState(0);
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const [showLoader, setShowLoader] = useState(false);
    const [fixedDateDisplay, setFixedDateDisplay] = useState("");
    const [canFetchMoreMessages] = useAtom(serviceSync.atomCanFetchMoreMessages);
    const [trackingFixedDate, setTrackingFixedDate] = useState(false);
    const [visibleConversationAndMessageMap] = useAtom(serviceSync.atomVisibleConversationAndMessageMap);

    const dateTopOffsetMapRef = useRef(new SortedMap<string, { getElem: () => HTMLDivElement; display: string; }>());

    const scrollTopHandler = useCallback(async (scrollTop: number) => {
        if (!fixedToBottom && canFetchMoreMessages && scrollTop === 0) {
            setShowLoader(true);
            const res = (await onLoadOlderMessages?.()) || { type: "try_again" };
            if (res.type === "no_messages") {
                setShowLoader(false);
            }
            else if (res.type === "try_again") {
                setShowLoader(false);
                const currentScrollTop = scrollDivRef.current?.scrollTop || 0;
                if (currentScrollTop < 40) {
                    scrollTo({
                        top: 40,
                        behavior: "smooth"
                    });
                }
            }
            else if (res.type === "new_messages") {
                setShowLoader(false);
                const currentScrollTop = scrollDivRef.current?.scrollTop || 0;
                const frameHeight = scrollDivRef.current?.offsetHeight || 0;
                const messagesHeight = messageContainerRef.current?.offsetHeight || 0;
                setFixedToBottom(true);
                setFixedToBottomShift((messagesHeight - currentScrollTop) - frameHeight);
                res.onReady();
            }
        }
        if (trackingFixedDate) {
            const fixedDataReference = scrollTop + 13;
            const floorKey = dateTopOffsetMapRef.current.findFloorKey((id) => libMisc.getRelativeTop(dateTopOffsetMapRef.current.get(id)!.getElem(), scrollDivRef.current!), fixedDataReference);
            setFixedDateDisplay(floorKey ? dateTopOffsetMapRef.current.get(floorKey)!.display : "");
        }
    }, [onLoadOlderMessages, canFetchMoreMessages, trackingFixedDate, fixedToBottom]);

    const { ref: scrollDivRef, scrollTo } = useTrackScrollTop(scrollTopHandler);

    useEffect(() => {
        if (inflatedHeight > 0) {
            const frameHeight = scrollDivRef.current?.offsetHeight || 0;
            const messagesHeight = messageContainerRef.current?.offsetHeight || 0;
            scrollTo({
                top: messagesHeight - frameHeight - fixedToBottomShift,
                behavior: "instant"
            });
            setFixedToBottom(false);
            setInflatedHeight(0);
        }
    }, [inflatedHeight, fixedToBottomShift]);

    const firstNonFixedToBottomRef = useRef(true);

    useEffect(() => {
        if (fixedToBottom) {
            setInflatedHeight(messageContainerRef.current?.offsetHeight || 0);
            setFixedDateDisplay("");
            setTrackingFixedDate(false);
        }
        else {
            setTrackingFixedDate(true);
            if (firstNonFixedToBottomRef.current) {
                firstNonFixedToBottomRef.current = false;
                serviceSync.openMobileThread();
            }
        }
    }, [fixedToBottom]);

    const [messageThreadScrollToBottomCounter] = useAtom(serviceSync.atomMessageThreadScrollToBottomCounter);

    useEffect(() => {
        if (messageThreadScrollToBottomCounter > 0 && scrollDivRef.current) {
            scrollDivRef.current.scrollTop = scrollDivRef.current.scrollHeight;
            scrollDivRef.current.classList.add("hide-scrollbars");
            setTimeout(() => {
                scrollDivRef.current?.classList.remove("hide-scrollbars");
            }, 300);
        }
    }, [messageThreadScrollToBottomCounter]);

    const messagesContainerElem = <div ref={messageContainerRef} className={s(`flex flex-col py-[15px] gap-[10px] justify-end min-h-full`, {
        "absolute left-0 right-0": fixedToBottom
    })} style={fixedToBottom ? { bottom: `${-fixedToBottomShift}px` } : {}}>
        <div className={s("flex justify-center items-center overflow-hidden transition-all duration-150", {}, [[
            "h-[40px]", "h-0", canFetchMoreMessages
        ]])}>
            <div className={s("transition-all duration-150", {}, [
                ["opacity-100", "opacity-0", showLoader]
            ])}>
                <Loader />
            </div>
        </div>
        {messages.map((message, index) => (
            <MessagingThreadMessage
                key={message.tmp_id}
                message={message}
                lastMessageTimestamp={(index > 0) ? messages[index - 1].timestamp : 0}
                onReportOffsetTop={(getElem, id, display) => {
                    if (getElem) {
                        if (scrollDivRef.current) {
                            dateTopOffsetMapRef.current.set(id, {
                                getElem,
                                display
                            });
                        }
                    }
                    else {
                        dateTopOffsetMapRef.current.delete(id);
                    }
                }}
                searchHighlight={!!visibleConversationAndMessageMap.messages[message.id]}
            />
        ))}
    </div>;

    const { text: draftText, mediaBlobIds: draftMediaBlobIds } = serviceSync.getDraft();

    return (
        <div className={`flex flex-col h-full`}>
            <div className="flex-grow relative">
                <div ref={scrollDivRef} className={s("absolute left-0 top-0 w-full h-full overflow-y-auto", {
                    "hide-scrollbars": fixedToBottom
                })}
                    onScroll={(e) => {
                        const divElem = e.target as HTMLDivElement;
                        serviceSync.trackOpenThreadScrollFromBottom(divElem.scrollHeight - divElem.offsetHeight - divElem.scrollTop);
                    }}
                >
                    <div style={{
                        height: `${inflatedHeight}px`
                    }}></div>
                    {!fixedToBottom && messagesContainerElem}
                </div>
                {fixedToBottom && (
                    <div className="absolute left-0 top-0 w-full h-full overflow-hidden">
                        {messagesContainerElem}
                    </div>
                )}
                {fixedDateDisplay !== "" && (
                    <div className="absolute left-0 right-0 top-0 pointer-events-none">
                        <MessagingThreadMessageDateCategoryDisplay display={fixedDateDisplay} />
                    </div>
                )}
                <MessagingThreadFailedActionMenu />
            </div>
            <div className="bg-[#f6f6f6] border-t border-gray-300" style={{
                flexBasis: `${159 + EXTRA_LINE_HEIGHT * addonLines}px`
            }}>
                <MessagingThreadSender initialText={draftText} initialImageBlobIds={draftMediaBlobIds} onTextAreaLineCountChange={(count) => {
                    setAddonLines(Math.min(MAXIMUM_TEXTAREA_LINES - MINIMUM_TEXTAREA_LINES, Math.max(0, count - MINIMUM_TEXTAREA_LINES)));
                }} />
            </div>
        </div>
    );
}